import { MapContext } from "context/MapContext";
import { useEffect, useState, useContext, memo } from "react";

import {
  AdvancedMarker,
  InfoWindow,
  useAdvancedMarkerRef,
  Pin,
} from "@vis.gl/react-google-maps";
import {
  SvgClose,
  SvgMapAlert,
  SvgMapMarker,
  SvgRobot,
  SvgGuardBox,
  SvgTailer,
  SvgPresetMarker,
  SvgAlertMapIcon,
} from "ui/svgs";
import type { Marker } from "@googlemaps/markerclusterer";
type Poi = {
  key: string;
  location: google.maps.LatLngLiteral;
  type: string;
  disableInfoWindow?: boolean;
  title?: string;
};

const Mark = ({
  poi,
  setMarkerRef,
  name,
  onClickMarker,
}: {
  poi: Poi;
  setMarkerRef: (marker: Marker | null, key: string) => void;
  name?: string;
  onClickMarker?: (poi: Poi) => void;
}) => {
  const { sites, currentSiteId } = useContext(MapContext);
  const [infowindowOpen, setInfowindowOpen] = useState(false);
  const [markerRef, marker] = useAdvancedMarkerRef();
  useEffect(() => {
    return () => {
      setInfowindowOpen(false);
    };
  }, [currentSiteId]);
  function getPin(poi) {
    const { type, title } = poi;
    switch (type) {
      case "trailer":
        return <SvgTailer />;
      case "robot":
        return <SvgRobot />;
      case "guardbox":
        return <SvgGuardBox />;
      case "alert":
        return (
          <div>
            <div
              style={{
                textAlign: "center",
                width: "40px",
                transform: "translateY(12px)",
              }}
            >
              {title}
            </div>
            <SvgMapAlert width={40} />
          </div>
        );
      case "preset":
        return (
          <div
            style={{
              textAlign: "center",
              width: "40px",
              transform: "translateY(20px)",
            }}
          >
            <SvgPresetMarker />
          </div>
        );
      case "alertMap":
        return (
          <div
            style={{
              textAlign: "center",
              alignContent: "center",
              alignItems: "center",
              cursor: "unset",
              width: "100px",
              transform: "translateY(25px)",
            }}
          >
            <SvgAlertMapIcon width={100} />
            <span
              className=""
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "white",
                textShadow: `-1px -1px 0 black,  1px -1px 0 black, -1px 1px 0 black, 1px 1px 0 black`,
              }}
            >
              {title}
            </span>
          </div>
        );
      default:
        return (
          <Pin
            background={"#3E83F6"}
            glyphColor={"#fff"}
            borderColor={"#fff"}
          />
        );
    }
  }
  return (
    <>
      <AdvancedMarker
        key={poi.key}
        position={poi.location}
        ref={(marker) => {
          //console.log("marker ref set: ", marker);
          markerRef(marker);
          setMarkerRef(marker, poi.key);
        }}
        clickable={true}
        onClick={() => {
          !poi.disableInfoWindow && setInfowindowOpen(true);
          onClickMarker?.(poi);
        }}
      >
        {getPin(poi)}
        {infowindowOpen && (
          <InfoWindow
            anchor={marker}
            maxWidth={200}
            onCloseClick={() => setInfowindowOpen(false)}
            className="w-full"
          >
            <div className="flex justify-between">
              <h2>
                {name ??
                  poi.title ??
                  sites.find((site) => site.id === +poi.key)?.name}
              </h2>
              <button
                className="ml-4 mr-2 cursor-pointer"
                onClick={() => {
                  setInfowindowOpen(false);
                }}
              >
                <SvgClose width={"14"} className="fill-neutral-800" />
              </button>
            </div>
            {/* This is an example for the <code style={{ whiteSpace: "nowrap" }}>&lt;AdvancedMarker /&gt;</code> combined with an Infowindow. */}
          </InfoWindow>
        )}
      </AdvancedMarker>
    </>
  );
};

export default memo(Mark);
